import { useEffect, useRef, useState } from 'react'
import {
  NativeSyntheticEvent,
  TextInputChangeEventData,
  View,
} from 'react-native'
import { useTranslation } from 'react-i18next'
import { NumberUtils } from '@ca/common'
import { ModalBottom } from '@ui/components/Modal'
import Input from '@ui/forms/Input'
import Button from '@ui/buttons/Button'
import CodeScannerInput from '@/common/CodeScanner/CodeScannerInput'
import { useQrScanner } from '@/common/CodeScanner/useQrScanner'
import { trpc } from 'api'
import { useToast } from '@ui/toasts/useToastManager'
import usePayments from '@order/hooks/usePayments'
import { useCurrencyDecimals } from 'hooks/useCurrency'
import { useStaff } from 'hooks/useSession'
import {
  createGlobemedAttempt,
  isDefiniteRejection,
} from 'features/payment/globemed/globemedAttempt'
import { fetchGlobemedMetadata } from 'features/payment/globemed/globemedMetadata'

const parseAmount = (value: string, max: number, decimals: number) => {
  const amount = Number(value.trim().replace(',', '.'))
  if (!Number.isFinite(amount) || amount <= 0) {
    return null
  }
  const rounded = NumberUtils.round.decimal(amount, decimals)
  return rounded > 0 && rounded <= max ? rounded : null
}

interface Props {
  open: boolean
  close: () => void
  numericOrderIds: number[]
  localId: string
  maxAmount: number
  // The limit may still change until ERP's range has loaded
  isRangeLoading: boolean
  // A removed GlobeMed payment; a new amount updates it instead of adding one
  draftPaymentId: number | null
}

export default function GlobeMedAddModal({
  open,
  close,
  numericOrderIds,
  localId,
  maxAmount,
  isRangeLoading,
  draftPaymentId,
}: Props) {
  const { t } = useTranslation(['payment', 'auth'])
  const text = 'payment:globemed.add'

  const [staffId, setStaffId] = useState('')
  const [amountInput, setAmountInput] = useState('')
  // Frozen on Apply; the balance limit drops once the apply lands
  const [submittedAmount, setSubmittedAmount] = useState<number | null>(null)

  // Scanned staff IDs are dropped unless the scanner mode allows them
  const setQrMode = useQrScanner((s) => s.setMode)
  useEffect(() => {
    if (!open) return
    setQrMode('dynamic')
    return () => {
      setQrMode('disabled')
    }
  }, [open, setQrMode])

  const toast = useToast()
  const utils = trpc.useUtils()
  const setMetadata = usePayments((s) => s.setMetadata)
  const setGlobemedAttempt = usePayments((s) => s.setGlobemedAttempt)
  const markOrdersSubmitted = usePayments((s) => s.markOrdersSubmitted)

  const decimals = useCurrencyDecimals()
  const amount = parseAmount(amountInput, maxAmount, decimals)
  const showAmountError =
    submittedAmount === null && amountInput.trim() !== '' && amount === null

  // ERP prepaid amount right before the request, to tell later if it landed
  const baselinePrepaid = useRef<number | null>(null)
  const [isPreparing, setIsPreparing] = useState(false)

  const apply = trpc.order.payments.globemed.useMutation({
    onSuccess: async (res, input) => {
      setGlobemedAttempt(
        input.order_ids,
        createGlobemedAttempt({
          amount: input.amount,
          paymentId: res.payment_id ?? input.payment_id ?? null,
          status: 'applied',
          operation: 'apply',
          baselinePrepaid: baselinePrepaid.current,
        })
      )
      // ERP moves the orders to sale on apply, so a later submit would fail
      markOrdersSubmitted(input.order_ids)
      await utils.order.payments.metadata.invalidate()
      const result = await fetchGlobemedMetadata(input.order_ids)
      if (result) {
        setMetadata(localId, result)
        toast({ variant: 'success', text: t(`${text}.toast.success`) })
      } else {
        toast({ variant: 'error', text: t(`${text}.toast.metadataError`) })
      }
      handleClose()
    },
    onError: async (error, input) => {
      if (isDefiniteRejection(error.data?.code)) {
        setSubmittedAmount(null)
        toast({ variant: 'error', text: t(`${text}.toast.error`) })
        return
      }
      // The request may still have reached ERP; block retries until resolved
      setGlobemedAttempt(
        input.order_ids,
        createGlobemedAttempt({
          amount: input.amount,
          paymentId: input.payment_id ?? null,
          status: 'uncertain',
          operation: 'apply',
          baselinePrepaid: baselinePrepaid.current,
        })
      )
      await utils.order.payments.metadata.invalidate()
      toast({ variant: 'error', text: t(`${text}.toast.uncertain`) })
      handleClose()
    },
  })

  // Confirms the logged in staff is still at the device; the server checks too
  const staff = useStaff()
  const isPending = isPreparing || apply.isPending
  const canApply =
    !!staffId.trim() && amount !== null && !isRangeLoading && !apply.isSuccess

  const handleApply = async () => {
    const ref = staffId.trim()
    if (!canApply || amount === null || isPending) return
    if (ref !== `${staff?.ref ?? ''}`.trim()) {
      toast({ variant: 'error', text: t('auth:staff.error.invalid') })
      return
    }
    setSubmittedAmount(amount)
    setIsPreparing(true)
    const before = await fetchGlobemedMetadata(numericOrderIds)
    baselinePrepaid.current = before?.prepaid_amount ?? null
    setIsPreparing(false)
    apply.mutate({
      order_ids: numericOrderIds,
      amount,
      staff_ref: ref,
      payment_id: draftPaymentId ?? undefined,
    })
  }

  const handleClose = () => {
    setStaffId('')
    setAmountInput('')
    setSubmittedAmount(null)
    baselinePrepaid.current = null
    apply.reset()
    close()
  }

  const handleUserClose = () => {
    if (isPending) return
    handleClose()
  }

  return (
    <ModalBottom
      hideBottomSpace
      open={open}
      close={handleUserClose}
      title={t(`${text}.title`)}
      desc={t(`${text}.desc`)}
      spacedText
      body={
        <View className="flex flex-col gap-12 px-16 pb-16">
          <Input
            label={t(`${text}.amount.label`)}
            value={amountInput}
            onChange={(e: NativeSyntheticEvent<TextInputChangeEventData>) =>
              setAmountInput(e.nativeEvent.text)
            }
            keyboardType="decimal-pad"
            errorMessage={
              showAmountError ? t(`${text}.amount.error`) : undefined
            }
            editable={!isPending}
            keyboardAppearance="dark"
          />
          <Input
            label={t('auth:staffId.label')}
            value={staffId}
            onChange={(e: NativeSyntheticEvent<TextInputChangeEventData>) =>
              setStaffId(e.nativeEvent.text)
            }
            action={<CodeScannerInput onSubmit={setStaffId} />}
            autoCapitalize="none"
            autoCorrect={false}
            onSubmitEditing={handleApply}
            enterKeyHint="done"
            editable={!isPending}
            keyboardAppearance="dark"
          />

          <View className="flex flex-row items-center gap-8">
            <Button
              variant="filled"
              label={t(`${text}.cta.apply`)}
              onPress={handleApply}
              loading={isPending || isRangeLoading}
              disabled={!canApply}
            />
            <Button
              variant="outlined"
              label={t(`${text}.cta.cancel`)}
              onPress={handleUserClose}
              disabled={isPending}
            />
          </View>
        </View>
      }
    />
  )
}
